import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTitle from "../PageTitle";
import useAxios from "../../utilities/useAxios";
import AttendanceClockSyncButton from "./AttendanceClockSyncButton";
import AttendanceDayExceptions from "./AttendanceDayExceptions";
import AttendanceDayOverrideModal from "./AttendanceDayOverrideModal";
import AttendanceLegend from "./AttendanceLegend";
import AttendancePersonCard from "./AttendancePersonCard";
import {
	buildSortedAttendanceRows,
	groupAttendanceRows,
} from "./attendanceHelpers";
import {
	Card,
	DatePicker,
	Space,
	Typography,
	message,
} from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Button, ConfigProvider } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import { antdKoKRSunday as locale } from "../../utilities/weekLocale";
import useIsMobile from "../../hooks/useIsMobile";
import PageHeader from "../common/PageHeader";

dayjs.locale("ko");

const { Text } = Typography;

const DailyAttendance = () => {
	const isMobile = useIsMobile();
	const [date, setDate] = useState(dayjs());
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [overrideTarget, setOverrideTarget] = useState(null);

	const loadStatus = useCallback(async () => {
		try {
			setLoading(true);
			const res = await useAxios.get("/attendance/status/", {
				params: {
					view: "daily",
					date: date.format("YYYY-MM-DD"),
				},
			});
			setData(res.data);
		} catch {
			message.error("일별 출근 현황을 불러오지 못했습니다.");
		} finally {
			setLoading(false);
		}
	}, [date]);

	useEffect(() => {
		loadStatus();
	}, [loadStatus]);

	const dateMeta = data?.dates?.[0];
	const dateKey = dateMeta?.date || date.format("YYYY-MM-DD");
	const rowBlocks = useMemo(
		() => groupAttendanceRows(buildSortedAttendanceRows(data?.employees || [])),
		[data?.employees],
	);

	const openOverride = (employee, cell) => {
		if (!cell?.can_override) return;
		setOverrideTarget({
			user_id: employee.user_id,
			name: employee.name,
			date: dateKey,
			kind: cell.override_kind || "일반출근",
			override_kind: cell.override_kind,
		});
	};

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="일별 출근 현황" />
			<div className="page">
				<PageHeader
					title="일별 출근 현황"
				/>
				<Card className="surface-card" loading={loading}>
					<div className="attendance-toolbar">
						<Space>
							<Button
								icon={<LeftOutlined />}
								onClick={() =>
									setDate((prev) => {
										const next = prev.subtract(1, "day");
										return next.day() === 0
											? next.subtract(1, "day")
											: next;
									})
								}
							/>
							<DatePicker
								inputReadOnly={isMobile}
								value={date}
								onChange={(value) => value && setDate(value)}
								allowClear={false}
								disabledDate={(current) => current?.day() === 0}
							/>
							<Button
								icon={<RightOutlined />}
								onClick={() =>
									setDate((prev) => {
										const next = prev.add(1, "day");
										return next.day() === 0
											? next.add(1, "day")
											: next;
									})
								}
							/>
						</Space>
						<Space wrap>
							<AttendanceClockSyncButton />
							<AttendanceLegend />
						</Space>
				</div>
				{dateMeta ? (
					<div style={{ marginBottom: 12 }}>
						<Text type="secondary">
							{dateMeta.work_type_name
								? `${dateMeta.work_type_name} · 출근 ${String(dateMeta.start_time || "").slice(0, 5)}`
								: "근무 유형 없음"}
							{dateMeta.holiday_name
								? ` · ${dateMeta.holiday_name}`
								: ""}
							{data?.is_sunday
								? " · 일요일은 근태에서 제외됩니다."
								: ""}
						</Text>
					</div>
				) : null}
				<section className="attendance-daily__exceptions">
					<AttendanceDayExceptions
						employees={data?.employees || []}
						date={dateKey}
						onCellClick={openOverride}
					/>
				</section>
				<section className="attendance-daily__roster">
				<div className="attendance-daily__roster-title">출근 현황</div>
				<div className="attendance-people attendance-people--board">
					{rowBlocks.map((block) =>
						block.type === "separator" ? (
							<div className="attendance-people__separator" key={block.key} />
						) : (
							<div className="attendance-people__group" key={block.key}>
								<div className="attendance-people__label">{block.group}</div>
								<div className="attendance-people__cards">
									{block.employees.map((employee) => (
										<AttendancePersonCard
											key={employee.user_id}
											employee={employee}
											cell={employee.cells?.[dateKey]}
											onCellClick={openOverride}
										/>
									))}
								</div>
							</div>
						),
					)}
					{!loading && rowBlocks.length === 0 ? (
						<Text type="secondary">등록된 교직원 정보가 없습니다.</Text>
					) : null}
				</div>
				</section>
			</Card>
			<AttendanceDayOverrideModal
				open={Boolean(overrideTarget)}
				target={overrideTarget}
				onClose={() => setOverrideTarget(null)}
				onSaved={loadStatus}
			/>
			</div>
		</ConfigProvider>
	);
};

export default DailyAttendance;
